// TicketSearch.js
import React, { useState, useEffect, useCallback } from 'react';
import apiJiraService from '../../services/jira.apiservice';
import { useParams, useNavigate } from 'react-router-dom';
import { FluxCircularProgress } from '@meltwater/flux-web-components-react';
import JiraSearch from './jiraSearch';
import OpportunityTable from './opportunityTable';
import styles from './jira.module.css';
import { Paper, Box, Typography, Divider, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import logger from '../../utils/logger';
const asyncRetry = require('async-retry'); // Adjust the path accordingly

const Jirarelatedtickets = () => {
  const { opId, optouse } = useParams();
  const navigate = useNavigate();
  logger.debug("Related tickets initialized", { opId, optouse });
  const [data, setData] = useState({});
  const [jiraerror,setjiraError] = useState(false)
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    // Set the title when the component mounts
    document.title = `Related Tickets - ${opId || 'Opportunity Search'}`;
    
    // Optionally, you can reset the title when the component unmounts
    return () => {
      document.title = 'GSD Support Admin';
    };
  }, [opId]);

  const fetchData = useCallback(async (opId) => {
    setLoading(true);
    let retryCount = 0;
    try {
      const results = await asyncRetry(
        async (bail) => {
          const response = await apiJiraService.getrelatedTickets(opId,optouse);

          if (response.status === 'ok') {
            logger.info('Related tickets found successfully', { 
              opId, 
              optouse, 
              ticketCount: response.data?.tickets?.length || 0 
            });
            
            setData(response.data);
          } else {
            logger.warn('Related tickets search failed', { 
              opId, 
              optouse, 
              status: response.status,
              statusCode: response.statusCode 
            });
            if (response.statusCode === 429) {
              // Retry only for 429 status code
              logger.debug('Rate limit exceeded, retrying...', { 
                opId, 
                attempt: retryCount + 1 
              });
              retryCount++;
              // Signal async-retry to retry
          
              bail(new Error('Rate limit exceeded'));
              return; // This line is essential to prevent further code execution in this iteration
            } else {
              setjiraError(true);
              return;
            }
          }
        },
        {
          retries: 3, // Number of retries
          minTimeout: 1000, // Minimum delay between retries in milliseconds
          maxTimeout: 5000, // Maximum delay between retries in milliseconds
          factor: 2, // Exponential backoff factor
        }
      );

      // Handle any post-retry logic or success-related tasks here
      logger.debug('Related tickets response complete', { opId, results });

      setLoading(false);
    } catch (error) {
      setjiraError(true);
      setLoading(false);
      // Handle errors or retries exhausted
      logger.error('Error fetching related tickets data', { 
        opId, 
        optouse, 
        error: error.message,
        response: error.response?.data 
      });
      if (error.message === 'Rate limit exceeded') {
        // Handle rate limit exceeded error if needed
      } else {
      }
    }
  }, [setData, setLoading, optouse]);

  useEffect(() => {
    const fetchDataEffect = async () => {
      if (opId) {
        await fetchData(opId);
      } else {
        // Handle case where opId is not available
      }
    };

    fetchDataEffect();
  }, [opId, fetchData]);

  return (
    <div className={styles.Jira} style={{ fontFamily: 'Inter, Roboto, Arial, sans-serif', background: '#f6f8fa', minHeight: '100vh', padding: '20px' }}>
      <div className={styles.divStyle}>
      
        {/* Top Bar Section */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} px={2} py={1} style={{ background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', minHeight: 56, border: '2px solid #28bcbb' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box display="flex" gap={1}>
              <Button 
                onClick={() => navigate(-1)}
                startIcon={<ArrowBackIcon />}
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: '#1d9f9f',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#188182',
                  }
                }}
              >
                Back
              </Button>
              <Button
                onClick={() => navigate('/jira')}
                variant="contained"
                size="small"
                sx={{
                  minWidth: '40px',
                  padding: '6px',
                  backgroundColor: '#1d9f9f',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#188182',
                  }
                }}
              >
                <HomeIcon fontSize="small" />
              </Button>
            </Box>
            <Typography variant="h5" style={{ fontWeight: 600, letterSpacing: 0.5, color: '#1a1a1a' }} className={styles.subheader}>
              {opId ? `Related Tickets for ${opId}` : 'Opportunity Search'}
            </Typography>
          </Box>
        </Box>
        
        {jiraerror &&  
          <Paper 
            elevation={3}
            sx={{
              background: '#fff',
              borderRadius: 3,
              p: 4,
              mx: 2,
              textAlign: 'center',
              boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
              border: '2px solid #28bcbb'
            }}
          >
            <Typography variant="h4" color="error" fontWeight={600} mb={2}>
              No Results Found
            </Typography>
            <Typography variant="h6" color="text.secondary" mb={3}>
              No tickets found for ID: {opId}
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={4}>
              Please enter a valid Account or Opportunity ID
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={4}>
              Contact Team Triton for more information
            </Typography>
            
            {/* Search Component for retry */}
            <Box sx={{ maxWidth: '600px', margin: '0 auto' }}>
              <JiraSearch />
            </Box>
          </Paper>
        }
        
        {loading && 
          <Paper elevation={0} style={{ borderRadius: 14, background: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.07)', marginLeft: '20px', marginRight: '20px', padding: 32, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <FluxCircularProgress indeterminate size="400">
              Loading related company data...
            </FluxCircularProgress>
          </Paper>
        }
        
        {!loading && !jiraerror && 
          <Paper elevation={3} style={{ borderRadius: 14, background: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.07)', padding: 32, border: '2px solid #28bcbb' }}>
            <Box mb={3}>
              <Typography variant="h6" className={styles.subheader} style={{ fontWeight: 600, color: '#1a1a1a' }}>Related Tickets</Typography>
              <Divider sx={{ my: 1 }} />
            </Box>
            <OpportunityTable transformedData={data} />
          </Paper>
        }
      </div>
    </div>
  );
};

export default Jirarelatedtickets;
